import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileUp, Upload } from 'lucide-react';

type FileUploadProps = {
  onFileAccepted: (file: File) => void;
  fileType: string;
  maxSize?: number;
  label?: string;
};

const FileUpload: React.FC<FileUploadProps> = ({
  onFileAccepted,
  fileType = '.xlsx',
  maxSize = 10485760, // 10MB default
  label = 'Upload Excel File'
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      onFileAccepted(acceptedFiles[0]);
    }
  }, [onFileAccepted]);

  const {
    getRootProps,
    getInputProps,
    isDragReject,
    fileRejections
  } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxSize,
    maxFiles: 1,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDropAccepted: () => setIsDragActive(false),
    onDropRejected: () => setIsDragActive(false)
  });

  const errorMessage = fileRejections.length > 0 ? 
    fileRejections[0].errors[0].message : '';

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`flex flex-col items-center justify-center w-full p-6 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-200 ease-in-out
          ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}
          ${isDragReject ? 'border-red-500 bg-red-50' : ''}
        `}
      >
        <input {...getInputProps()} />
        
        <div className={`w-16 h-16 mb-3 rounded-full flex items-center justify-center transition-colors duration-200
          ${isDragActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}
          ${isDragReject ? 'bg-red-100 text-red-600' : ''}
        `}>
          {isDragActive ? <Upload size={28} /> : <FileUp size={28} />}
        </div>
        
        <p className="mb-2 text-sm font-semibold text-gray-700">{label}</p>
        <p className="text-xs text-gray-500">
          Drag & drop an {fileType} file or click to browse
        </p>
        <p className="mt-1 text-xs text-gray-400">
          (Maximum file size: {Math.round(maxSize / 1048576)}MB)
        </p>
      </div>

      {errorMessage && (
        <p className="mt-2 text-sm text-red-600">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default FileUpload;